import {Form, Formik} from 'formik'
import {FC, useCallback, useEffect, useState} from 'react'
import {
  CAR_DATE_FILTER_TYPE,
  CarFilteredDTO,
  ICarFilterModel, 
  initialCarFilter,
  TIRE_WARNING_KM,
  validationCarFilter,
} from './CarModel'
import {
  branches,
  fetchAsyncCarData,
  vehicleGroups,
  vehicleOwners,
} from '../../../shared/models/SharedApiLookups'
import {showErrorToast} from '../../../shared/components/ToastUtils'
import {filterCars} from './Api'
import GroupSelect from '../../../shared/components/GroupSelect'
import SelectInput from '../../../shared/components/SelectInput'
import {ACTIVE_OR_PASSIVE} from '../../../shared/models/SharedConstantLookups'
import DateInput from '../../../shared/components/DateInput'
import {ToastContainer} from 'react-toastify'
import {CarTable} from './CarTable'
import AsyncSearchInput from '../../../shared/components/AsyncSearchInput'
import {AsyncCarModel} from '../../../shared/models/ApiModels'

export const CarListPage: FC = () => {
  const [cars, setCars] = useState<CarFilteredDTO[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [vehicleGroupOptions, setVehicleGroupOptions] = useState<{value: string; label: string}[]>(
    []
  )
  const [vehicleOwnerOptions, setVehicleOwnerOptions] = useState<{value: string; label: string}[]>(
    []
  )
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize, setPageSize] = useState(10)
  const [filterValues] = useState<ICarFilterModel>(initialCarFilter)
  const [filterOpen, setFilterOpen] = useState(true)

  useEffect(() => {
    Promise.all([branches(), vehicleGroups(), vehicleOwners()]).then(([brn, vg, vo]) => {
      setBranchOptions(brn)
      setVehicleGroupOptions(vg)
      setVehicleOwnerOptions(vo)
    })
  }, [])

  const filter = useCallback(
    async (values: ICarFilterModel, page: number = 0, size: number = pageSize, sort?: string, order?: 'asc' | 'desc') => {
      try {
        setLoading(true)
        const res = await filterCars(values, page, size, sort, order)
        const data = res.data.payload || res.data
        setCars(data?.content ?? [])
        setTotalPages(data?.totalPages ?? 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Araçlar yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        filter(filterValues, newPage, pageSize, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, filter, filterValues, pageSize]
  )

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size)
      setCurrentPage(0)
      filter(filterValues, 0, size, sortBy, sortOrder)
    },
    [filterValues, sortBy, sortOrder, filter]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      filter(filterValues, currentPage, pageSize, column, order)
    },
    [currentPage, pageSize, filterValues, filter]
  )

  const handleFilter = async (values: ICarFilterModel) => {
    filter(values, currentPage, pageSize, sortBy, sortOrder)
  }

  return (
    <>
      <div className='card'>
        {/* Card Header - Filtre Bölümü */}
        <div className='card-header border-0 pt-6 flex-wrap gap-3'>
          <div className='card-title flex-grow-1'>
            <div className='d-flex align-items-center gap-3 my-1'>
              <span className='symbol symbol-45px'>
                <span className='symbol-label bg-light-primary'>
                  <i className='bi bi-funnel fs-4 text-primary' aria-hidden />
                </span>
              </span>
              <div>
                <h3 className='fw-bold text-gray-800 m-0 fs-4'>Araç filtreleri</h3>
                <div className='text-muted fs-7 fw-semibold mt-1'>
                  Listeyi daraltmak için kriterleri seçin
                </div>
              </div>
            </div>
          </div>

          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFilterOpen((prev) => !prev)}
              aria-expanded={filterOpen}
            >
              <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              {filterOpen ? 'Gizle' : 'Göster'}
            </button>
          </div>
        </div>

        {/* Collapsible Filter Form */}
        <div className={`collapse ${filterOpen ? 'show' : ''}`}>
          <div className='card-body pt-0 pb-6 px-6 px-lg-8'>
            <Formik
              initialValues={initialCarFilter}
              enableReinitialize
              validationSchema={validationCarFilter}
              onSubmit={handleFilter}
            >
              {(formik) => (
                <Form id='carFilterForm'>
                  <div className='card shadow-sm border border-gray-300'>
                    <div className='card-body bg-light'>
                      {/* Plaka, durum, lastik */}
                      <div className='row g-4 mb-2'>
                        <AsyncSearchInput<AsyncCarModel>
                          name='car'
                          label='Plaka / araç'
                          labelPosition='top'
                          placeholder='Plaka veya araç arayın...'
                          fetchHandler={fetchAsyncCarData}
                          displayField={(item) => `${item.plate} (${item.modelYear})`}
                          onSelect={(item) => formik.setFieldValue('carId', item.id)}
                          onClear={() => formik.setFieldValue('carId', null)}
                          wrapperClassName='col-12 col-md-6 col-xl-4'
                        />
                        <SelectInput
                          name='status'
                          label='Araç durumu'
                          labelPosition='top'
                          options={ACTIVE_OR_PASSIVE}
                          wrapperClassName='col-12 col-md-6 col-xl-4'
                        />
                        <SelectInput
                          name='tireWarningKm'
                          label='Lastik uyarısı (KM)'
                          labelPosition='top'
                          firstItemLabel='Lastik uyarı KM seçiniz'
                          options={TIRE_WARNING_KM}
                          wrapperClassName='col-12 col-xl-4'
                        />
                      </div>

                      {/* Şube ve grup: telefonda alt alta, sm+ yan yana */}
                      <div className='row g-3 g-md-4 mb-2 align-items-stretch'>
                        <GroupSelect
                          name='officeIdList'
                          label='Şubeler (çoklu)'
                          labelPosition='top'
                          options={branchOptions}
                          wrapperClassName='col-12 col-sm-6 d-flex flex-column'
                        />
                        <GroupSelect
                          name='vehicleGroupIdList'
                          label='Araç Grupları (çoklu)'
                          labelPosition='top'
                          options={vehicleGroupOptions}
                          wrapperClassName='col-12 col-sm-6 d-flex flex-column'
                        />
                      </div>

                      {/* Sahip ve tarih */}
                      <div className='row g-4'>
                        <SelectInput
                          name='vehicleOwner'
                          label='Araç sahibi'
                          labelPosition='top'
                          options={vehicleOwnerOptions}
                          wrapperClassName='col-12 col-md-6 col-xl-3'
                        />
                        <SelectInput
                          name='dateFilterType'
                          label='Tarih filtresi'
                          labelPosition='top'
                          firstItemLabel='Tarih önemsiz'
                          options={CAR_DATE_FILTER_TYPE}
                          wrapperClassName='col-12 col-md-6 col-xl-3'
                        />
                        <DateInput
                          name='startDate'
                          type='date'
                          label='Başlangıç'
                          labelPosition='top'
                          wrapperClassName='col-12 col-sm-6 col-xl-3'
                        />
                        <DateInput
                          name='endDate'
                          type='date'
                          label='Bitiş'
                          labelPosition='top'
                          wrapperClassName='col-12 col-sm-6 col-xl-3'
                        />
                      </div>

                      <div className='separator separator-dashed my-6' />

                      <div className='d-flex flex-wrap justify-content-end gap-3'>
                        <button
                          type='button'
                          className='btn btn-light btn-active-light-primary'
                          onClick={() => formik.resetForm()}
                        >
                          Temizle
                        </button>

                        <button type='submit' className='btn btn-primary' disabled={loading}>
                          {loading ? (
                            <span className='indicator-progress' style={{display: 'block'}}>
                              Lütfen bekleyin...
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          ) : (
                            <>
                              <i className='ki-duotone ki-magnifier fs-2'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                              </i>
                              Filtrele
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Card Body - Table */}
        <div className='card-body border-top border-gray-200 pt-6'>
          <CarTable
            data={cars}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageSizeChange={(size) => handlePageSizeChange(size)}
            onPageChange={handlePageChange}
            onEdit={(r) => console.log('edit', r)}
            onDelete={(r) => console.log('delete', r)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={loading}
          />
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}
